import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import TopBackNavBar from '../../components/BackNavBar';
import BodyText from '../../components/BodyText';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import { RedButton } from '../../components/Button';
import { Pages } from '../../App';
import { setTaxErrorData } from '../../DumbleData';
import useUpdateSessionInfo from '../../utils/updateSessionInfo';
import { ConfigContext } from '../../providers/ConfigContext';
import TextInput from '../../components/TextInput';
import useMakeSale from '../../hooks/useMakeSale';
import { SaleResponseInfo } from '../../interfaces/sale-response-info';
import Bookfair from '../../interfaces/bookfair-info';
import PaymentPayload from '../../interfaces/payment-payload';

interface Props {
	pInfo: PaymentInfo;
	bfInfo: Bookfair;
	isTaxExemptChecked: boolean;
	setBookfairInfo: any;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	setTransactionId: React.Dispatch<React.SetStateAction<string>>;
	setSaleResponseInfo: React.Dispatch<React.SetStateAction<SaleResponseInfo>>;
}

const Checks = ({ pInfo, bfInfo, isTaxExemptChecked, setBookfairInfo, setCurrentPage, setTransactionId, setSaleResponseInfo }: Props) => {
	const { apiBase } = useContext(ConfigContext);
	const [status, setStatus] = useState<number>(200);
	const [makeSale] = useMakeSale();
	const [isButtonProcessing, setIsButtonProcessing] = useState(false);
	const [checkNumber, setCheckNumber] = useState<string>('');
	const [checkError, setCheckError] = useState<boolean>(false);
	const { getSessionInfo } = useUpdateSessionInfo();

	// Check numbers can only be numeric up to 15 digits
	const handleChange = (value: string) => {
		const re = /^[0-9]{0,15}$/;
		if (re.test(value)) {
			setCheckNumber(value);
		}
	};

	// Submits Checks Payment
	const handleChecksPayment = async () => {
		// If check number is left empty, show an error
		if (!checkNumber.length) {
			setCheckError(true);
			return;
		}
		setIsButtonProcessing(true);
		setCheckError(false);
		const payload: PaymentPayload = {
			total: {
				amount: pInfo.amountDue.toNumber(),
				tax: {
					amount: pInfo.tax.toNumber(),
					rate: bfInfo.taxRate.toNumber(),
					taxableSubtotal: isTaxExemptChecked ? 0 : pInfo.itemTotal.toNumber(),
					status: bfInfo.taxStatus,
					selectedExempt: isTaxExemptChecked,
				},
			},
			tenders: {
				check: {
					amount: pInfo.amountDue.toNumber(),
					id: checkNumber,
				},
			},
		};
		const { data, error } = await makeSale(payload);
		if (error) {
			if (error.status === 401) {
				setCurrentPage(Pages.Landing);
			}
			setIsButtonProcessing(false);
			setStatus(error.status);
			console.error(error);
		} else if (data != null) {
			setTransactionId(data.parentTransactionId);
			setSaleResponseInfo(data);
			window._satellite.track('checks-payment-submit');
			setCurrentPage(Pages.Confirmation);
		}
	};

	// If there is a mismatch with tax values, retrieve up-to-date tax info and send to new purchase page
	const handleTaxDiscrepancy = async () => {
		const success = await getSessionInfo(apiBase, setBookfairInfo);
		if (success) {
			setCurrentPage(Pages.NewPurchase);
		} else {
			setCurrentPage(Pages.Landing);
		}
	};

	// Determine error message based on error code
	let errorMessage: React.ReactNode = null;
	if (status === 422) {
		setTaxErrorData(true);
		errorMessage = (
			<>
				{'There appears to be a discrepancy in the sales tax amount. In order to proceed, please '}
				<StyledLink onClick={handleTaxDiscrepancy}>click here</StyledLink>
				{' to recalculate the sales tax.'}
			</>
		);
	} else if (status !== 200) {
		errorMessage = 'We are unable to process your check. Please use a different payment method or call 877-245-0903.';
	}

	return (
		<>
			<TopBackNavBar setCurrentPage={setCurrentPage} title="Check Payment" location={Pages.PaymentMethod} />
			<ChecksPage>
				<ContentWrapper>
					<CalculationsDiv>
						<RowItemDiv>
							<ItemLabel>Item Total</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.itemTotal.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Tax</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.tax.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Amount Due</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.amountDue.toNumber())}</ItemValue>
						</RowItemDiv>
						<StyledInputDiv>
							<TextInput value={checkNumber} onChange={handleChange} label="Check Number" errorText="This field is required." error={checkError}></TextInput>
						</StyledInputDiv>
					</CalculationsDiv>
					<StyledRedButton disabled={isButtonProcessing || status !== 200} handleClick={handleChecksPayment}>
						Submit Payment
					</StyledRedButton>
					<PayableText>Checks should be made payable to the school.</PayableText>
					{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
				</ContentWrapper>
			</ChecksPage>
		</>
	);
};

const ChecksPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const PayableText = styled.p`
	font-size: 13px;
	font-weight: 500;
	line-height: 15px;
	margin-top: 12px;
	margin-bottom: 0;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 280px;
	margin-top: 53px;
`;

const CalculationsDiv = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 38px;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;

const ItemLabel = styled(BodyText)`
	display: flex;
	align-items: center;
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	display: flex;
	align-items: center;
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;

const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 0;
`;

const StyledRedButton = styled(RedButton)`
	width: 100%;
	margin: 0;
`;

const ErrorText = styled(BodyText)`
	font-size: 13px;
	margin: 20px 0 0 5px;
	text-align: left;
`;

const StyledLink = styled.a`
	text-decoration: underline;
	cursor: pointer;
`;

const StyledInputDiv = styled.div`
	margin-top: 13px;
	width: 100%;
`;

export default Checks;
